import { useMemo } from 'react';
import { UseFormReset } from 'react-hook-form';

import { DEFAULT_COUNTRY, DEFAULT_REQUIRED_FIELDS } from 'addresses/constants';
import { AddressFields } from 'addresses/enums';
import { useAuthUser } from 'auth/hooks';
import { ContactGroupType } from 'contacts/enums';
import { useContactGroupActions } from 'contacts/hooks';
import { IContactGroup, IContactGroupAddress, ILocationForm } from 'contacts/types';
import { NotificationPosition } from 'core/enums';
import { FormProvider, useNotificationContext } from 'core/states';

import { mapLocationForm } from './mappers';
import { RemoveLocationButton } from './RemoveLocationButton';
import { locationSchema } from './schema';
import { UpsertLocationFormFields } from './UpsertLocationFormFields';

interface IProps {
  isOpen: boolean;
  onSubmitSuccess?: (address?: IContactGroupAddress) => void;
  onClose: () => void;
  initialValues?: ILocationForm;
  location?: IContactGroupAddress;
  contactGroup?: Partial<IContactGroup>;
  contactGroupType?: ContactGroupType;
  level?: number;
  requiredAddressFields?: AddressFields[];
}

export const UpsertLocation = ({
  isOpen,
  onClose,
  onSubmitSuccess,
  initialValues,
  location,
  contactGroup,
  contactGroupType,
  level,
  requiredAddressFields = DEFAULT_REQUIRED_FIELDS,
}: IProps) => {
  const { clearNotification } = useNotificationContext();
  const { user } = useAuthUser();

  const { upsertContactGroupAddress } = useContactGroupActions();

  const isAlternateVersionEnabled = Boolean(user?.alternateVersionEnabled);

  const defaultValues = useMemo(() => {
    if (!location?.id) {
      return {
        isShipTo: true,
        isBillTo: !isAlternateVersionEnabled,
        country: DEFAULT_COUNTRY,
        contactGroup: contactGroup,
        ...initialValues,
      };
    }

    const customerFields =
      contactGroupType === ContactGroupType.CUSTOMER
        ? {
            customerNumber: location.externalCustomerNumber || null,
          }
        : {};

    return {
      addressId: location.addressId,
      isShipTo: Boolean(location.isShipTo),
      isBillTo: Boolean(location.isBillTo),
      ...customerFields,
      line1: location.line1,
      line2: location.line2,
      name: location.name,
      city: location.city,
      country: location.countryId
        ? {
            name: location.countryName,
            abbreviation: location.countryAbbreviation,
            id: location.countryId,
          }
        : DEFAULT_COUNTRY,
      state: location.stateId
        ? {
            name: location.stateName,
            abbreviation: location.stateAbbreviation,
            id: location.stateId,
          }
        : null,
      zip: location.zip,
      contactGroup: location.contactGroup,
    };
  }, [contactGroup, contactGroupType, initialValues, isAlternateVersionEnabled, location]);

  const onSubmit = (reset: UseFormReset<ILocationForm>) => async (data: ILocationForm) => {
    const { success, contactGroupAddress } = await upsertContactGroupAddress(
      mapLocationForm(data, contactGroupType)
    );

    if (!success) {
      reset(data);

      return;
    }

    setTimeout(() => {
      onSubmitSuccess?.(contactGroupAddress);
      handleOnClose(reset);
    }, 1000);
  };

  const handleOnClose = (reset: UseFormReset<ILocationForm>) => {
    clearNotification();
    onClose();

    reset(defaultValues);
  };

  const removeButton = useMemo(() => {
    return location ? (
      <RemoveLocationButton
        locations={[location]}
        notificationPosition={NotificationPosition.FORM}
        onSubmitSuccess={() => {
          onClose();
          onSubmitSuccess?.();
        }}
      />
    ) : null;
  }, [location, onClose, onSubmitSuccess]);

  return (
    <FormProvider
      schema={locationSchema(contactGroupType, isAlternateVersionEnabled)}
      defaultValues={defaultValues}
      resetDefaultValues
    >
      <UpsertLocationFormFields
        isOpen={isOpen}
        onClose={handleOnClose}
        onSubmit={onSubmit}
        contactGroup={contactGroup}
        contactGroupType={contactGroupType}
        isEdit={!!location?.id}
        level={level}
        extraButtons={removeButton}
        requiredAddressFields={requiredAddressFields}
      />
    </FormProvider>
  );
};
